import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTags, getAllTags, deleteTag as deleteTagApi } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import { entityStoreMessages } from './storeHelpers';
import { useCustomerStoreBase, useArchivedCustomerStoreBase } from './customerStore';

export interface Tag {
  id: string;
  name: string;
  color: string;
  category: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    assignments: number;
  };
}

// 標籤管理頁（分頁）
const useTagStoreBase = createEntityStore<Tag>({
  entityName: 'tag',
  fetchFn: (page: number) => getTags(page),
  deleteFn: deleteTagApi,
  toast,
  messages: entityStoreMessages,
});

/**
 * 全部標籤（客戶表單的標籤選擇器、客戶列表的標籤篩選用）
 * 不能用管理頁的分頁 store：那只有 20 筆，而且是管理頁最後停留的那一頁
 */
interface AllTagsState {
  allTags: Tag[];
  isLoading: boolean;
  lastFetch: number | null;
  fetchAllTags: () => Promise<void>;
}

let allTagsRequest: Promise<void> | null = null;

export const useAllTagsStore = create<AllTagsState>()(
  persist(
    (set, get) => ({
      allTags: [],
      isLoading: false,
      lastFetch: null,
      // stale-while-revalidate：有快取先用、背景重抓（同 customerStore fetchAllCustomers）
      fetchAllTags: async () => {
        if (allTagsRequest) return allTagsRequest;
        if (!get().lastFetch) set({ isLoading: true });
        allTagsRequest = (async () => {
          try {
            const response = await getAllTags();
            set({ allTags: response.data, lastFetch: Date.now() });
          } catch (error) {
            console.error('載入標籤失敗:', error); // 失敗時保留快取
          } finally {
            set({ isLoading: false });
            allTagsRequest = null;
          }
        })();
        return allTagsRequest;
      },
    }),
    {
      name: 'all-tags-store',
      partialize: (state) => ({ allTags: state.allTags, lastFetch: state.lastFetch }),
    }
  )
);

/** 標籤 ID 換成標籤物件（樂觀更新客戶的 tags 用；後端回應的客戶 tags 是物件陣列） */
export const tagsByIds = (ids: string[]): Tag[] => {
  const allTags = useAllTagsStore.getState().allTags;
  return ids.map(id => allTags.find(tag => tag.id === id)).filter((tag): tag is Tag => !!tag);
};

/** 標籤新增、編輯、刪除後：選擇器重新載入全部標籤，並通知資料異動 */
export const notifyTagsChanged = () => {
  useAllTagsStore.getState().fetchAllTags();
  notifyDataChanged('tags');
};

export { useTagStoreBase };

export const useTagStore = () => {
  const store = useTagStoreBase();

  // 刪除後客戶列表上的同一個標籤也拿掉（後端由外鍵連帶刪除指派）
  const confirmDelete = async () => {
    const tagId = store.itemToDeleteId;
    await store.confirmDelete();
    if (tagId) {
      [useCustomerStoreBase, useArchivedCustomerStoreBase].forEach(customerStore =>
        customerStore.setState((state: any) => ({
          items: state.items.map((customer: any) => ({
            ...customer,
            tags: (customer.tags || []).filter((tag: Tag) => tag.id !== tagId),
          })),
        }))
      );
    }
    notifyTagsChanged();
  };

  return {
    tags: store.items,
    pagination: store.pagination,
    error: store.error,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    isDeleting: store.isDeleting,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete,
    closeDeleteModal: store.closeDeleteModal,
    fetchTags: store.fetchItems,
    addTag: store.addItem,
    updateTag: store.updateItem,
  };
};
